import { ref, watch, onBeforeUnmount } from 'vue'
import { useDebounceFn } from '@vueuse/core'

/**
 * @param {(() => any) | import('vue').Ref} source
 * @param {(val:any) => (Promise<void>|void)} save
 * @param {{
 *   enabled?: () => boolean,
 *   debounceMs?: number,
 *   flushOnBlur?: boolean,
 *   flushOnVisibilityChange?: boolean
 * }} opts
 */
export function useAutoSave (source, save, opts = {}) {
  const enabled = opts.enabled || (() => true)
  const debounceMs = opts.debounceMs ?? 800
  const flushOnBlur = opts.flushOnBlur !== false
  const flushOnVisibilityChange = opts.flushOnVisibilityChange !== false

  const saving = ref(false)
  const error = ref(null)

  const sourceGetter = typeof source === 'function' ? source : () => source?.value
  const clone = (v) => JSON.parse(JSON.stringify(v))

  let lastSaved = clone(sourceGetter())

  const doSave = async () => {
    if (!enabled()) return
    const val = sourceGetter()
    if (JSON.stringify(val) === JSON.stringify(lastSaved)) return
    saving.value = true
    error.value = null
    try {
      await save(val)
      lastSaved = clone(val)
    } catch (e) {
      error.value = e?.message || 'Save failed'
    } finally {
      saving.value = false
    }
  }

  const debounced = useDebounceFn(doSave, debounceMs)

  // Save after user pauses typing
  watch(sourceGetter, () => {
    if (!enabled()) return
    debounced()
  })

  // Flush on blur / tab hide / unload / Cmd/Ctrl+S
  const blurHandler = () => { void doSave() }
  const visHandler = () => { if (document.visibilityState === 'hidden') void doSave() }
  const unloadHandler = () => { void doSave() }
  const keyHandler = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault()
      void doSave()
    }
  }

  if (flushOnBlur) window.addEventListener('blur', blurHandler)
  if (flushOnVisibilityChange) document.addEventListener('visibilitychange', visHandler)
  window.addEventListener('beforeunload', unloadHandler)
  window.addEventListener('keydown', keyHandler)

  onBeforeUnmount(() => {
    if (flushOnBlur) window.removeEventListener('blur', blurHandler)
    if (flushOnVisibilityChange) document.removeEventListener('visibilitychange', visHandler)
    window.removeEventListener('beforeunload', unloadHandler)
    window.removeEventListener('keydown', keyHandler)
    void doSave()
  })

  return { saving, error, flush: doSave }
}
